/* eslint-disable max-classes-per-file */
import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';

import { ckeditorApi } from '@/services/api/ckeditorService';
import {
  Comment as ApiComment,
  DetailedCommentThread as ApiDetailedCommentThread,
} from '@/services/types/ckeditor';
import { store } from '@/state/state';

const {
  CKEditor: { Plugin, Editor: _Editor },
  CKEditorPremiumFeatures: {
    CommentsRepository: _CommentsRepository,
    Comment: _Comment,
  },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

// CKEditor types
type Editor = InstanceType<typeof _Editor>;
type Comment = InstanceType<typeof _Comment>;
type CommentsRepository = InstanceType<typeof _CommentsRepository>;
type CommentsRepositoryAdapter = CommentsRepository['adapter'];
type CommentAdded = Parameters<CommentsRepositoryAdapter['addComment']>[0];
type CommentUpdated = Parameters<CommentsRepositoryAdapter['updateComment']>[0];
type CommentThreadAdded = Parameters<
  CommentsRepositoryAdapter['addCommentThread']
>[0];
type CommentThreadUpdated = Parameters<
  CommentsRepositoryAdapter['updateCommentThread']
>[0];
type CommentData = ReturnType<Comment['toJSON']>;

interface CommentsIntegrationConfig {
  documentId: string;
}

// Convert CKEditor comment format to our API format for new or updated comments
const convertToNewComment = (
  authorId: string,
  comment: CommentAdded | CommentUpdated,
): ApiComment => {
  return {
    author_id: authorId,
    comment_id: comment.commentId,
    thread_id: comment.threadId,
    content: comment.content,
    attributes: comment.attributes || {},
  };
};

// Convert CKEditor comment data to our API format
const convertToComment = (
  thread_id: string,
  comment: CommentData,
): ApiComment => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    comment_id: comment.commentId!,
    thread_id,
    content: comment.content,
    attributes: comment.attributes || {},
    created_at: comment.createdAt.toISOString(),
    author_id: comment.authorId,
  };
};

// Convert our API comment format to CKEditor format
const convertFromComment = (comment: ApiComment): CommentData => {
  return {
    commentId: comment.comment_id,
    content: comment.content || '',
    authorId: comment.author_id || '',
    createdAt: comment.created_at ? new Date(comment.created_at) : new Date(),
    attributes: comment.attributes || {},
  };
};

// Convert CKEditor thread format to our API format for a new thread
const convertToCommentThread = (
  thread: CommentThreadAdded,
): ApiDetailedCommentThread => {
  return {
    thread_id: thread.threadId,
    attributes: thread.attributes || {},
    context: thread.context ? thread.context : {},
    unlinked_at: thread.unlinkedAt
      ? new Date(thread.unlinkedAt).toISOString()
      : undefined,
    resolved_at: thread.resolvedAt
      ? new Date(thread.resolvedAt).toISOString()
      : undefined,
    resolved_by: thread.resolvedBy || undefined,
    created_at: undefined,
    comments: thread.comments.map((c) => convertToComment(thread.threadId, c)),
  };
};

// Create partial comment thread for updates
const convertToCommentThreadUpdate = (
  thread: CommentThreadUpdated,
): Partial<ApiDetailedCommentThread> => {
  return {
    thread_id: thread.threadId,
    attributes: thread.attributes,
    context: thread.context ? thread.context : {},
    unlinked_at: thread.unlinkedAt
      ? new Date(thread.unlinkedAt).toISOString()
      : undefined,
  };
};

// Convert our API thread format to CKEditor format
const convertFromCommentThread = (
  thread: ApiDetailedCommentThread,
): {
  threadId: string;
  attributes: Record<string, unknown>;
  context: Record<string, unknown>;
  unlinkedAt: Date | null;
  resolvedAt: Date | null;
  resolvedBy: string | null;
  createdAt: Date;
  comments: CommentData[];
  isFromAdapter: boolean;
} => {
  return {
    threadId: thread.thread_id,
    attributes: thread.attributes || {},
    context: thread.context || {},
    unlinkedAt: thread.unlinked_at ? new Date(thread.unlinked_at) : null,
    resolvedAt: thread.resolved_at ? new Date(thread.resolved_at) : null,
    resolvedBy: thread.resolved_by || null,
    createdAt: thread.created_at ? new Date(thread.created_at) : new Date(),
    comments: thread.comments.map(convertFromComment),
    isFromAdapter: true,
  };
};

class CommentsIntegration extends Plugin {
  private documentId: string;

  private api: typeof ckeditorApi;

  static get requires() {
    return ['CommentsRepository', 'UsersIntegration'];
  }

  static get pluginName() {
    return 'CommentsIntegration';
  }

  constructor(editor: Editor) {
    super(editor);
    const config = editor.config.get(
      'commentsIntegration',
    ) as CommentsIntegrationConfig;
    this.documentId = config.documentId;
    this.api = ckeditorApi;
  }

  init() {
    const commentsRepositoryPlugin =
      this.editor.plugins.get('CommentsRepository');
    const usersPlugin = this.editor.plugins.get('Users');

    commentsRepositoryPlugin.adapter = {
      addComment: async (data) => {
        const authorId = usersPlugin.me?.id || 'anonymous-user';
        const comment = convertToNewComment(authorId, data);

        const result = await store.dispatch(
          this.api.endpoints.createComment.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
            comment,
          }),
        );

        if ('error' in result || !result.data) {
          throw new Error('Failed to add comment');
        }

        return {
          commentId: result.data.comment_id,
          createdAt: result.data.created_at
            ? new Date(result.data.created_at)
            : new Date(),
        };
      },

      updateComment: async (data) => {
        const authorId = usersPlugin.me?.id || 'anonymous-user';
        const comment = convertToNewComment(authorId, data);

        const result = await store.dispatch(
          this.api.endpoints.updateComment.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
            commentId: data.commentId,
            comment,
          }),
        );

        if ('error' in result) {
          throw new Error('Failed to update comment');
        }
      },

      removeComment: async (data) => {
        const result = await store.dispatch(
          this.api.endpoints.deleteComment.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
            commentId: data.commentId,
          }),
        );

        if ('error' in result) {
          throw new Error('Failed to remove comment');
        }
      },

      addCommentThread: async (data) => {
        const thread = convertToCommentThread(data);

        const result = await store.dispatch(
          this.api.endpoints.createCommentThread.initiate({
            documentId: this.documentId,
            thread,
          }),
        );

        if ('error' in result || !result.data) {
          throw new Error('Failed to add comment thread');
        }

        return {
          threadId: result.data.thread_id,
          comments: result.data.comments.map((comment) => ({
            commentId: comment.comment_id,
            createdAt: comment.created_at
              ? new Date(comment.created_at)
              : new Date(),
          })),
        };
      },

      getCommentThread: async (data) => {
        const result = await store.dispatch(
          this.api.endpoints.getCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
          }),
        );

        if ('error' in result || !result.data) {
          throw new Error('Failed to get comment thread');
        }

        return convertFromCommentThread(result.data);
      },

      updateCommentThread: async (data) => {
        // First get the existing thread to preserve comments
        const getResult = await store.dispatch(
          this.api.endpoints.getCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
          }),
        );

        if ('error' in getResult || !getResult.data) {
          throw new Error('Failed to update comment thread - thread not found');
        }

        // Merge the updates with existing data
        const updatedThread = {
          ...getResult.data,
          ...convertToCommentThreadUpdate(data),
        };

        const result = await store.dispatch(
          this.api.endpoints.updateCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
            thread: updatedThread,
          }),
        );

        if ('error' in result) {
          throw new Error('Failed to update comment thread');
        }
      },

      resolveCommentThread: async (data) => {
        // First get the existing thread
        const getResult = await store.dispatch(
          this.api.endpoints.getCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
          }),
        );

        if ('error' in getResult || !getResult.data) {
          throw new Error(
            'Failed to resolve comment thread - thread not found',
          );
        }

        // Use a safe way to get the current user ID
        const currentUserId = usersPlugin.me?.id || 'anonymous-user';
        const resolvedAt = new Date();

        // Update the thread with resolved status
        const updatedThread = {
          ...getResult.data,
          resolved_at: resolvedAt.toISOString(),
          resolved_by: currentUserId,
        };

        const result = await store.dispatch(
          this.api.endpoints.updateCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
            thread: updatedThread,
          }),
        );

        if ('error' in result) {
          throw new Error('Failed to resolve comment thread');
        }

        return {
          threadId: data.threadId,
          resolvedAt,
          resolvedBy: currentUserId,
        };
      },

      reopenCommentThread: async (data) => {
        // First get the existing thread
        const getResult = await store.dispatch(
          this.api.endpoints.getCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
          }),
        );

        if ('error' in getResult || !getResult.data) {
          throw new Error('Failed to reopen comment thread - thread not found');
        }

        // Update the thread to remove resolved status
        const updatedThread = {
          ...getResult.data,
          resolved_at: undefined,
          resolved_by: undefined,
        };

        const result = await store.dispatch(
          this.api.endpoints.updateCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
            thread: updatedThread,
          }),
        );

        if ('error' in result) {
          throw new Error('Failed to reopen comment thread');
        }
      },

      removeCommentThread: async (data) => {
        const result = await store.dispatch(
          this.api.endpoints.deleteCommentThread.initiate({
            documentId: this.documentId,
            threadId: data.threadId,
          }),
        );

        if ('error' in result) {
          throw new Error('Failed to remove comment thread');
        }
      },
    };
  }
}

export default CommentsIntegration;
